import { PropsWithChildren, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { camelize } from 'humps';

import ConditionalRenderer from '../ConditionalRenderer';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import SimpleTextIcon from '../SimpleTextIcon';
import Popup from '../Popup';
import Text from '../dataDisplay/Text';

export type HiddenWrapperProps = PropsWithChildren & {
  render?: boolean;
  className?: Pick<ClassNameComponent, 'base'>;
  setRef?: (ref: HTMLElement) => void;
};

// TODO: replace hidden components with this component
export default function HiddenWrapper(props: HiddenWrapperProps) {
  const { render = true, className, children, setRef } = props;

  return (
    <ConditionalRenderer condition={render}>
      <section
        ref={setRef}
        className={twMerge(
          'rounded-2xl border-neutral-content shadow-default transition-all ease-in-out duration-300 border bg-base-100 w-full flex flex-col',
          className?.base,
        )}
      >
        {children}
      </section>
    </ConditionalRenderer>
  );
}

type HeaderProps = PropsWithChildren & {
  title: string;
  icon?: ReactNode;
  className?: Pick<ClassNameComponent, 'header' | 'aside'>;
  header?: ReactNode;
};

function Header(props: HeaderProps) {
  const { className, title, icon, header, children } = props;

  return (
    <header
      className={twMerge(
        'flex justify-between items-center text-accent w-full relative p-4',
        className?.header,
      )}
    >
      <SimpleTextIcon
        title={title}
        icon={icon}
        truncate
        tooltip
        className={{ base: 'max-w-[80%]', tooltip: 'self-center' }}
      />
      <div className="flex gap-2 relative -right-1.5">
        {header}
        {children}
      </div>
    </header>
  );
}

export type HiddenOption = {
  title: string;
  icon?: ReactNode;
  onClick?(): void;
};

type OptionsProps = {
  options: HiddenOption[];
  className?: Pick<
    ClassNameComponent,
    'reference' | 'popup' | 'ul' | 'li' | 'text'
  >;
};

function Options(props: OptionsProps) {
  const { options, className } = props;

  return (
    <ConditionalRenderer condition={options.length}>
      <Popup
        className={{
          reference: className?.reference,
          popup: twMerge('z-10', className?.popup),
        }}
        reference={
          <EllipsisVerticalIcon data-testid="hideSectionPopup" className="h-6" />
        }
        hover
      >
        <ul
          className={twMerge(
            'bg-base-100 rounded-xl p-2.5 border border-neutral-content text-base-content w-52',
            className?.ul,
          )}
        >
          {options.map(option => (
            <li
              role="button"
              key={camelize(option.title)}
              data-testid={camelize(option.title)}
              onClick={option.onClick}
              className={twMerge(
                'flex gap-3.5 items-center cursor-pointer hover:text-accent transition ease-in-out duration-300 select-none bg-base-100',
                className?.li,
              )}
            >
              {option.icon}

              <Text
                text={option.title}
                format="rubik-400"
                className={twMerge('text-16', className?.text)}
              />
            </li>
          ))}
        </ul>
      </Popup>
    </ConditionalRenderer>
  );
}

type SectionProps = PropsWithChildren & {
  className?: Pick<ClassNameComponent, 'section'>;
};

function Section(props: SectionProps) {
  const { className, children } = props;
  return (
    <section className={twMerge('flex flex-col gap-3.5', className?.section)}>
      {children}
    </section>
  );
}

HiddenWrapper.Header = Header;
HiddenWrapper.Options = Options;
HiddenWrapper.Section = Section;
